import {
  AutoCompleteShipInfo,
  extractTargetInfos,
  getOverlayMP,
  GetOverlayProps,
  getShipInfo,
  getTargetInfoByMMSI,
  useFindTargetByProperties,
  useSession,
} from '@ais-ui/websockets';
import { useEffect, useRef, useState } from 'react';
import {
  addPulseToCoordinate,
  getDimensionsFromMap,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { fromLonLat } from 'ol/proj';
import { useAISMapContext } from './AISMapContext';
import { useTimeZoneContext } from '@ais-ui/utils';
import { useToast } from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useSelectionState } from '../../components/SelectionStateProvider';

function useOverlayAndMap(findTargetResult: any) {
  const [overlayProps, setOverlayProps] = useState<GetOverlayProps | null>(
    null
  );
  const context = useOpenLayersContext();
  const { toast } = useToast();
  const { messages } = useLocalization();

  useEffect(() => {
    if (!findTargetResult) {
      setOverlayProps(null);
      return;
    }

    if (findTargetResult.result === 'FindTargetNotFound') {
      toast({
        title: messages.genericToastTitleWarning,
        description: 'No target found',
        variant: 'warning',
      });
      setOverlayProps(null);
      return;
    }

    const coordinate = fromLonLat([findTargetResult.lon, findTargetResult.lat]);
    context.map.getView().setCenter(coordinate);

    const dimensions = getDimensionsFromMap(
      context.map,
      context.mapProjection,
      context.userProjection
    );

    if (dimensions) {
      const { center, zoomLevel, size, northEast, southWest } = dimensions;
      setOverlayProps({
        centerLat: center.lat,
        centerLon: center.lng,
        zoomLevel: zoomLevel,
        width: size.width,
        height: size.height,
        leftLon: northEast.lng,
        rightLon: southWest.lng,
      });
    }

    addPulseToCoordinate(context.map, coordinate, 3);
  }, [findTargetResult, context]);

  return overlayProps;
}

function useFetchShipInfo(
  isFetching: boolean,
  setIsFetching: (value: boolean) => void
) {
  const { setTargetInfoDetailsList } = useSelectionState();

  const { zoomToShip, setZoomToShip } = useAISMapContext();
  const { olSession } = useSession();
  const [fetchedShipInfoResult, setFetchedShipInfoResult] = useState(null);
  const { toast } = useToast();
  const { messages } = useLocalization();
  const shipAtFetchStart = useRef<AutoCompleteShipInfo | null>(null);

  // Capture the ship we're fetching for when isFetching becomes true
  useEffect(() => {
    if (isFetching) {
      shipAtFetchStart.current = zoomToShip;

      getShipInfo(olSession)
        .then((data) => {
          setFetchedShipInfoResult(data);
        })
        .catch((error) => {
          console.error('Failed to fetch ship info:', error);
        });
    }
  }, [isFetching, zoomToShip, olSession]);

  const processTargetInfo = async () => {
    if (!fetchedShipInfoResult) {
      return [];
    }

    const targetInfoResult = extractTargetInfos(fetchedShipInfoResult);

    // If we found target info, return it immediately
    if (targetInfoResult.extractedTargetInfo.length > 0) {
      return [targetInfoResult];
    }

    // Guard clause - if no MMSI, return empty list
    if (!shipAtFetchStart.current?.mmsi) {
      return [];
    }

    // Try to isFetching by MMSI as fallback
    const queryPointByMmsi = await getTargetInfoByMMSI(
      olSession,
      Number(shipAtFetchStart.current.mmsi)
    );
    const targetInfoResultByMmsi = extractTargetInfos(queryPointByMmsi);
    if (targetInfoResultByMmsi.extractedTargetInfo.length > 0) {
      return [targetInfoResultByMmsi];
    }

    toast({
      title: messages.genericToastTitleWarning,
      description: "Couldn't find ship info",
      variant: 'warning',
    });

    return [];
  };

  useEffect(() => {
    // Guard clause - exit early if no fetched results
    if (!fetchedShipInfoResult) {
      return;
    }
    // Create a separate async function inside useEffect
    const handleProcessTargetInfo = async () => {
      try {
        const targetInfoList = await processTargetInfo();
        setTargetInfoDetailsList(targetInfoList);
      } finally {
        setIsFetching(false);
        setFetchedShipInfoResult(null);
        setZoomToShip(null);
      }
    };

    handleProcessTargetInfo();
  }, [
    fetchedShipInfoResult,
    zoomToShip,
    setIsFetching,
    setTargetInfoDetailsList,
    toast,
    messages,
    processTargetInfo,
    setZoomToShip,
  ]);
}

export function useZoomAndSelectShip() {
  const { zoomToShip } = useAISMapContext();
  const { olSession } = useSession();
  const { timeZone } = useTimeZoneContext();
  const [isFetchingShipInfo, setIsFetchingShipInfo] = useState(false);

  useEffect(() => {
    if (!zoomToShip) {
      setIsFetchingShipInfo(false);
    }
  }, [zoomToShip]);

  const { data: findTargetResult } = useFindTargetByProperties(zoomToShip);

  const overlayProps = useOverlayAndMap(zoomToShip ? findTargetResult : null);
  useEffect(() => {
    const fetchOverlayData = async () => {
      if (overlayProps && zoomToShip) {
        try {
          await getOverlayMP(olSession, overlayProps, timeZone);

          // When filteredOut is true, the backend needs additional time to process
          // the ship data, especially when dealing with filtered/hidden ships that
          // may require extra validation or data retrieval steps
          const delayMs = zoomToShip.filteredOut ? 200 : 50;

          setTimeout(() => {
            setIsFetchingShipInfo(true);
          }, delayMs);
        } catch (error) {
          console.error('Failed to fetch overlay data:', error);
        }
      }
    };

    fetchOverlayData();
  }, [zoomToShip, olSession, overlayProps, timeZone]);

  useFetchShipInfo(isFetchingShipInfo, setIsFetchingShipInfo);
}
